import { CircleOutlined } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  IconButton,
  Typography,
  Button,
  Drawer,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Popover,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSignalR } from '@/app/shared/hooks/useSignalR';

import { AlertModal } from '@/app/shared/ui/AlertModal';
import Loader from '@/app/shared/ui/Loader';
import { formatCurrency, roundAccurately } from '@/app/shared/utils/helpers';
import { extraColor } from '@/app/styles/muiTheme';
import ContractIcon from '@/assets/images/handshake.svg?react';
import COMIcon from '@/assets/images/products/COM.svg?react';
import GAPIcon from '@/assets/images/products/GAP.svg?react';
import MBAIcon from '@/assets/images/products/MBA.svg?react';
import MBIIcon from '@/assets/images/products/MBI.svg?react';
import PAFIcon from '@/assets/images/products/PAF.svg?react';
import VRPIcon from '@/assets/images/products/VRP.svg?react';
import VSCIcon from '@/assets/images/products/VSC.svg?react';

import { useAppSelector } from '@/store/hooks';

import {
  useFetchCustomerProductsQuery,
  useUpdateCustomerProductsMutation,
  useRejectAllProductsMutation,
} from './products.services';
import {
  CardWrap,
  PricingCard,
  CornerRibbon,
  RibbonText,
  CardBody,
  TransitionContainer,
  StripesWrapper,
  CardFooter,
  SelectedButton,
  MobileRibbon,
} from './products.styles';
import { TimeNumber } from './products.types';
import type { ICustomerProductsResponse, IProduct, ICustomerCard, IRate } from './products.services';

const RIBBON_COLORS: Record<string, { color: string; shadow: string }> = {
  FULL: { color: '#1565C0', shadow: 'rgba(21,101,192,0.45)' },
  STANDARD: { color: '#C62828', shadow: 'rgba(198,40,40,0.45)' },
  BASE: { color: '#2E7D32', shadow: 'rgba(46,125,50,0.45)' },
};

const sectionLabel = {
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  mb: 0.5,
};

const PRODUCT_FULL_FORMS: Record<string, string> = {
  GAP: 'Guaranteed Asset Protection',
  VSC: 'Vehicle Service Contract',
  MBI: 'Mechanical Breakdown Insurance',
  PAF: 'Paint And Fabric',
  VRP: 'Vehicle Return Plan',
  COM: 'Comprehensive Coverage',
  MBA: 'Maintenance Benefit Agreement',
};

const PRODUCT_ICONS: Record<string, React.ElementType> = {
  GAP: GAPIcon,
  VSC: VSCIcon,
  MBI: MBIIcon,
  PAF: PAFIcon,
  VRP: VRPIcon,
  COM: COMIcon,
  MBA: MBAIcon,
};

enum PackageType {
  FULL = 'Full',
  STANDARD = 'Standard',
  BASE = 'Base',
}

const PACKAGE_INFO: Record<PackageType, string> = {
  [PackageType.FULL]: 'Total Peace Of Mind',
  [PackageType.STANDARD]: 'Most Popular',
  [PackageType.BASE]: 'Minimum Suggested',
};

const formatRate = (rate: IRate, cost: string): string => {
  const formatCost = formatCurrency(cost);
  const termPart = rate.maxTerm != null ? `${rate.minTerm} to ${rate.maxTerm}` : `${rate.minTerm} `;
  const milesPart = rate.maxMiles != null && rate.maxMiles > 0 ? ` or ${rate.maxMiles.toLocaleString()} miles ` : '';
  return `${termPart} months ${milesPart} ${formatCost}`;
};

const calcCardFinance = (
  products: IProduct[],
  loanTerm: number,
  interestRate: number
): { finance: number; costPerMonth: number } => {
  const monthlyInterest = interestRate / TimeNumber.MONTHS;

  const calcCostPerMonth = (retail: number): number => {
    if (!monthlyInterest || !loanTerm) return 0;
    return roundAccurately(
      (retail * (monthlyInterest * Math.pow(1 + monthlyInterest, loanTerm))) / (Math.pow(1 + monthlyInterest, loanTerm) - 1),
      2
    );
  };
  const total = products.reduce((sum, p) => sum + (p.retail ?? 0), 0);
  return {
    finance: products.reduce((sum, p) => sum + (p.retail ?? 0), 0),
    costPerMonth: calcCostPerMonth(total),
  };
};
interface SelectedEntryPanelProps {
  selectedEntry: IProduct;
}

const SelectedEntryPanel = ({ selectedEntry }: SelectedEntryPanelProps) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      background: '#fff',
    }}
  >
    <Typography
      fontWeight={700}
      fontSize={18}
    >
      {PRODUCT_FULL_FORMS[selectedEntry.productTypeName?.toUpperCase()] ?? selectedEntry.productTypeName}
    </Typography>

    <Box>
      <Typography sx={sectionLabel}>Plan</Typography>
      <Typography
        fontSize={15}
        fontWeight={500}
      >
        {selectedEntry.plan.planName}
      </Typography>
    </Box>
    <Divider />
    <Box>
      <Typography sx={sectionLabel}>Term / Miles</Typography>
      <Typography
        fontSize={14}
        fontWeight={500}
      >
        {formatRate(selectedEntry.rate, JSON.stringify(selectedEntry.cost))}
      </Typography>
    </Box>
    <Divider />
    <Box>
      <Typography sx={sectionLabel}>Deductible</Typography>
      <Typography
        fontSize={14}
        fontWeight={500}
      >
        {selectedEntry.deductible.name}
      </Typography>
    </Box>

    {selectedEntry.options.length > 0 && (
      <Box>
        <Divider />
        <Typography
          sx={sectionLabel}
          marginTop={2}
        >
          Options
        </Typography>
        <Box
          display="flex"
          flexDirection="column"
          gap={0.75}
        >
          {selectedEntry.options.map((option) => (
            <Typography
              key={option.id}
              fontSize={13}
              fontWeight={500}
            >
              {option.name}
              {option.isSurcharge && option.amount > 0 && (
                <Typography
                  component="span"
                  fontSize={13}
                  color="text.secondary"
                  ml={1}
                >
                  (+${option.amount.toFixed(2)})
                </Typography>
              )}
            </Typography>
          ))}
        </Box>
      </Box>
    )}

    <Box
      display="flex"
      justifyContent="space-between"
      width="100%"
      pt={2}
      mt="auto"
      sx={{ borderTop: '0.5px solid rgba(11, 81, 121, 0.32)', marginTop: '30px' }}
    >
      <Typography
        fontSize={13}
        fontWeight={600}
        color="text.secondary"
      >
        TOTAL:
      </Typography>
      <Typography
        fontSize={20}
        fontWeight={700}
      >
        {formatCurrency(selectedEntry.retail)}
      </Typography>
    </Box>
  </Box>
);

const Products = () => {
  const contactId = useAppSelector((state) => state.auth.contactId) || '';

  const {
    data: customerProducts = {} as ICustomerProductsResponse,
    refetch,
    isLoading,
  } = useFetchCustomerProductsQuery(contactId, {
    skip: !contactId,
    refetchOnMountOrArgChange: true,
  });

  const [updateCustomerProducts] = useUpdateCustomerProductsMutation();
  const [rejectAllProducts] = useRejectAllProductsMutation();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<IProduct | null>(null);

  const [modalType, setModalType] = useState<'submit' | 'decline' | null>(null);

  const [currentPackage, setCurrentPackage] = useState<ICustomerCard | null>(null);
  const [isSelectedEntryOpen, setIsSelectedEntryOpen] = useState(false);
  const { isConnected, notifications } = useSignalR(import.meta.env.REACT_APP_API_URL, customerProducts?.dealId || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isContracted = Array.isArray(customerProducts?.customerCards)
    ? customerProducts?.customerCards.some((card) => card?.products.some((product) => product?.isContracted === true))
    : false;

  const customerCards = useMemo<ICustomerCard[]>(() => {
    return customerProducts?.customerCards ?? [];
  }, [customerProducts]);

  const [expandedCard, setExpandedCard] = useState<string | false>(customerCards[0]?.cardName ?? false);
  const selectedCard = customerCards.find((c) => c.isSelected);
  const open = Boolean(anchorEl);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (selectedCard) {
      setSelectedPackage(selectedCard.cardName);
      setCurrentPackage(selectedCard);
      setExpandedCard(selectedCard.cardName);
    } else {
      setExpandedCard(customerCards[0]?.cardName ?? false);
    }
  }, [customerCards, selectedCard]);

  useEffect(() => {
    if (!isConnected || notifications.length === 0) return;
    refetch();
    setSelectedPackage('');
    setIsSelectedEntryOpen(false);
    setSelectedEntry(null);
  }, [notifications]);

  useEffect(() => {
    for (const card of customerCards) {
      const preSelected = card.products.find((p) => p.isSelected);
      if (preSelected) {
        setSelectedEntry(preSelected);
        break;
      }
    }
  }, [customerCards]);

  const handleAccordionChange = (cardName: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCard(isExpanded ? cardName : false);
  };

  const handleDeclineAll = async () => {
    const rejectAllProductsPayload = {
      id: customerProducts.id,
      isRejectAll: true,
      isFromPhoenix: false,
    };
    setIsDeclining(true);
    try {
      await rejectAllProducts(rejectAllProductsPayload).unwrap();
      refetch();
      setSelectedEntry(null);
      setModalType(null);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDeclining(false);
    }
  };

  const onConfirm = async () => {
    if (!currentPackage) return;

    const selectedCard = customerCards
      .map((card) =>
        card.cardName === currentPackage.cardName
          ? {
              cardName: card.cardName,
              isSelected: true,
              products: card.products.map((product) => ({
                dealProductId: product.dealProductId,
                isSelected: true,
              })),
            }
          : card
      )
      .find((card) => card.cardName === currentPackage.cardName);

    const requestPayload = {
      dealId: customerProducts.dealId,
      contactId: customerProducts.contactId,
      customerCards: selectedCard ? [selectedCard] : null,
      isFromPhoenix: false,
    };

    setIsSubmitting(true);
    try {
      await updateCustomerProducts({
        id: customerProducts.id,
        payload: requestPayload,
      }).unwrap();
      refetch();
      setSelectedPackage(currentPackage.cardName);
      setModalType(null);
      setSelectedEntry(null);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setModalType(null);
    setCurrentPackage(null);
  };

  const handleSelectCard = (card: ICustomerCard) => {
    if (isContracted) return;
    if (selectedPackage === card.cardName) {
      setSelectedPackage(null);
      setCurrentPackage(null);
      return;
    }
    setSelectedPackage(card.cardName);
    setCurrentPackage(card);
  };

  const renderSelectButtonContent = (cardName: string) => {
    if (isContracted) {
      return (
        <>
          <LockIcon
            fontSize="medium"
            sx={{ color: '#fff' }}
          />
          <Typography
            fontSize={13}
            color="#fff"
          >
            Contracted
          </Typography>
        </>
      );
    }

    return selectedPackage === cardName ? (
      <>
        <CheckCircleIcon
          fontSize="medium"
          sx={{ color: theme.palette.success.main }}
        />
        <Typography
          fontSize={13}
          fontWeight={600}
          color={extraColor.palette.green.main}
        >
          {cardName} Selected
        </Typography>
      </>
    ) : (
      <>
        <CircleOutlined
          fontSize="medium"
          sx={{ color: extraColor.palette.green.main }}
        />
        <Typography
          fontSize={13}
          color={extraColor.palette.green.main}
        >
          Select {cardName}
        </Typography>
      </>
    );
  };

  const desktopView = () => (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: { lg: 1, xl: 2 },
        maxWidth: { md: '100%', lg: '100%', xl: '90%', xxl: '80%' },
        margin: '0 auto',
      }}
    >
      <Box
        display="grid"
        gridTemplateColumns={'3.3fr 1.2fr '}
        gap={2}
        sx={{ p: 1, alignItems: 'start' }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          {customerCards.map((card) => {
            const financeSummary = calcCardFinance(
              card.products,
              customerProducts?.term ?? 1,
              customerProducts?.rate ? customerProducts.rate / 100 : 0
            );
            const ribbonKey = card.cardName.toUpperCase();
            const isCardSelected = selectedPackage === card.cardName;

            return (
              <Box
                key={card.cardName}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: { lg: '500px', xl: '600px' },
                  maxWidth: { lg: '460px', xl: '380px' },
                  '@media (min-width:1800px)': {
                    width: '380px',
                  },
                }}
              >
                <CardWrap
                  $selected={isCardSelected}
                  $locked={isContracted}
                  sx={{ cursor: isContracted ? 'default' : 'pointer' }}
                >
                  <PricingCard>
                    {RIBBON_COLORS[ribbonKey] && (
                      <CornerRibbon
                        $color={RIBBON_COLORS[ribbonKey].color}
                        $shadowColor={RIBBON_COLORS[ribbonKey].shadow}
                      >
                        <RibbonText>{PACKAGE_INFO[card.cardName as PackageType]}</RibbonText>
                      </CornerRibbon>
                    )}

                    <CardBody className="card-body">
                      <Box sx={{ p: { md: 2, lg: 2, xl: 3 }, pt: { md: 4, lg: 5 } }}>
                        <Typography
                          className="title-text"
                          variant="h1"
                          sx={{
                            fontStyle: 'italic',
                            fontWeight: 800,
                            fontSize: { lg: '1.4rem', xl: '3rem' },
                            color: isCardSelected ? '#0e59cf' : '#888',
                            lineHeight: 1,
                            transition: 'color 0.5s ease',
                          }}
                        >
                          {card.cardName.toUpperCase()}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: '#555', mt: 1.5, fontWeight: 500, fontSize: { lg: '1rem', xl: '1.5rem' } }}
                        >
                          COVERAGE
                        </Typography>
                        <Box
                          className="divider-line"
                          sx={{
                            width: 40,
                            height: 2,
                            backgroundColor: isCardSelected ? '#0e59cf' : '#888',
                            mt: 2,
                            transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
                          }}
                        />
                      </Box>

                      <Box
                        mt="auto"
                        display="flex"
                        justifyContent="center"
                        gap={1}
                        flexWrap="wrap"
                        sx={{
                          minHeight: 100,
                          alignContent: 'center',
                          px: 1,
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                        }}
                      >
                        {card.products.map((product) => {
                          const productCode = product.productTypeName?.toUpperCase();
                          const Icon = PRODUCT_ICONS[productCode];
                          const isHighlighted = selectedEntry?.dealProductId === product.dealProductId;
                          const isContractedProduct = product?.isContracted;
                          return (
                            <Box
                              key={product.dealProductId}
                              sx={{
                                border: isContractedProduct ? '1.4px #9ce752 solid' : '1px solid transparent',
                                borderRadius: '8px',
                                padding: '1px',
                              }}
                              
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEntry(product);
                              }}
                            >
                              <Box
                                sx={{
                                  border: isHighlighted ? '2px solid #0b5179' : '1px solid transparent',
                                  borderRadius: '8px',
                                  px: 1,
                                  py: 1,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: { lg: '70px', xl: '90px' },
                                  height: { md: '20%', lg: '70px', xl: '80px' },
                                  backgroundColor: '#ffff',
                                  position: 'relative',
                                }}
                              >
                                {isContractedProduct && (
                                  <span
                                    style={{
                                      position: 'absolute',
                                      top: '-7px',
                                      right: '-8px',
                                      padding: '2px 2px 0px 2px',
                                      background: '#fff',
                                      border: '1.5px #67a12f solid',
                                      borderRadius: '30px',
                                     cursor: 'pointer'
                                    }}
                                    onMouseEnter={isContractedProduct ? (e) => handlePopoverOpen(e) : undefined}
                                   onMouseLeave={isContractedProduct ? () => handlePopoverClose() : undefined}
                                  >
                                    <ContractIcon
                                      color="#5f8f31"
                                      width={13}
                                      height={13}
                                    />
                                  </span>
                                )}
                                {Icon && (
                                  <Icon
                                    width={70}
                                    height={70}
                                  />
                                )}
                                <Typography
                                  fontSize={{ lg: 13, xl: 15 }}
                                  mt={1}
                                >
                                  {productCode}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </CardBody>

                    <TransitionContainer $selected={isCardSelected}>
                      <StripesWrapper>
                        <Box sx={{ width: 16, backgroundColor: isCardSelected ? '#436fb0' : '#e7dfdf' }} />
                        <Box sx={{ width: 6, backgroundColor: '#fff' }} />
                        <Box sx={{ width: 6, backgroundColor: isCardSelected ? '#436fb0' : '#e7dfdf' }} />
                        <Box sx={{ width: 6, backgroundColor: '#fff' }} />
                        <Box sx={{ width: 20, backgroundColor: isCardSelected ? '#436fb0' : '#e7dfdf' }} />
                      </StripesWrapper>
                    </TransitionContainer>

                    <CardFooter
                      className="footer-gradient"
                      $selected={isCardSelected}
                    >
                      <Box
                        mt="auto"
                        py={1.5}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-end"
                        flexWrap="wrap"
                      >
                        <Box>
                          <Typography
                            fontSize={{ md: 15, lg: 17 }}
                            fontWeight={600}
                            letterSpacing="0.05em"
                            marginBottom={1}
                          >
                            FINANCE
                          </Typography>
                          <Typography
                            fontSize={{ md: 15, lg: 18, xl: 25 }}
                            fontWeight={700}
                          >
                            {formatCurrency(financeSummary.finance)}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography
                            fontSize={{ md: 13, lg: 13, xl: 20 }}
                            fontWeight={700}
                            ml={2}
                          >
                            ${financeSummary.costPerMonth}/mo
                          </Typography>
                        </Box>
                      </Box>

                      <SelectedButton
                        disableElevation
                        variant="contained"
                        isContracted={isContracted}
                        disabled={isContracted || (selectedCard && selectedCard.isSelected)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectCard(card);
                        }}
                      >
                        {renderSelectButtonContent(card.cardName)}
                      </SelectedButton>
                    </CardFooter>
                  </PricingCard>
                </CardWrap>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedEntry && <SelectedEntryPanel selectedEntry={selectedEntry} />}

          <Box
            sx={{
              mt: 'auto',
              padding: '10px',
            }}
          >
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => setModalType('decline')}
              disabled={isContracted}
              sx={{
                background: '#fff',
                borderRadius: 50,
                py: 1.5,
                fontSize: { md: '0.9rem', lg: '1rem' },
                whiteSpace: 'nowrap',
                marginBottom: '1rem',
              }}
            >
              Decline All
            </Button>
            <Button
              variant="contained"
              fullWidth
              disabled={!selectedPackage || isContracted || (selectedCard && selectedCard.isSelected)}
              onClick={() => setModalType('submit')}
              sx={{
                borderRadius: 50,
                py: 1.5,
                fontSize: { md: '0.9rem', lg: '1rem' },
                whiteSpace: 'nowrap',
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const mobileView = () => (
    <Box sx={{ p: 2 }}>
      {customerCards.map((card) => {
        const isPackageSelected = selectedPackage === card.cardName;

        const financeSummary = calcCardFinance(
          card.products,
          customerProducts?.term ?? 1,
          customerProducts?.rate ? customerProducts.rate / 100 : 0
        );
        const ribbonKey = card.cardName.toUpperCase();

        return (
          <Accordion
            key={card.cardName}
            expanded={expandedCard === card.cardName}
            onChange={handleAccordionChange(card.cardName)}
            disableGutters
            sx={{
              mb: 2,
              borderRadius: '16px !important',
              overflow: 'hidden',
              boxShadow: isPackageSelected ? '0 4px 16px rgba(0,82,204,0.18)' : 2,
              border: isPackageSelected ? '2px solid #436fb0' : '1px solid #e0e0e0',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                position: 'relative',
                minHeight: 90,
                borderLeft: `5px solid ${RIBBON_COLORS[ribbonKey]?.color ?? '#1565C0'}`,
              }}
            >
              <MobileRibbon $color={RIBBON_COLORS[ribbonKey]?.color ?? '#1565C0'}>
                {PACKAGE_INFO[card.cardName as PackageType]}
              </MobileRibbon>
              <Box>
                <Typography
                  fontWeight={800}
                  fontSize={{ xs: 18, sm: 20, md: 22 }}
                  fontStyle={'italic'}
                  sx={{ color: isPackageSelected ? '#0e59cf' : '#888', lineHeight: 1, transition: 'color 0.5s ease' }}
                >
                  {card.cardName.toUpperCase()}
                </Typography>
                <Typography
                  fontSize={{ xs: 8, sm: 10, md: 12 }}
                  letterSpacing="2px"
                >
                  COVERAGE
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Box
                display="flex"
                justifyContent="center"
                gap={1}
                flexWrap="wrap"
                mb={3}
              >
                {card.products.map((product) => {
                  const productCode = product.productTypeName?.toUpperCase();
                  const Icon = PRODUCT_ICONS[productCode];
                  const isHighlighted = selectedEntry?.dealProductId === product.dealProductId;
                  const isContractedProduct = product?.isContracted;
                  return (
                    <Box
                      key={product.dealProductId}
                      sx={{
                        border: isContractedProduct ? '1.4px #9ce752 solid' : '1px solid transparent',
                        borderRadius: '8px',
                        padding: '1px',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEntry(product);
                        setIsSelectedEntryOpen(true);
                      }}
                    >
                      <Box
                        key={product.dealProductId}
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: 2,
                          border: isHighlighted ? '2px solid #0b5179' : '1px solid transparent',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'border-color 0.2s',
                          padding: '5px 0px',
                          position: 'relative',
                        }}
                      >
                        {isContractedProduct && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              if (anchorEl) {
                                handlePopoverClose();
                              } else {
                                handlePopoverOpen(e);
                              }
                            }}
                            style={{
                              position: 'absolute',
                              top: '-7px',
                              right: '-8px',
                              padding: '2px 2px 0px 2px',
                              background: '#fff',
                              border: '1.5px #67a12f solid',
                              borderRadius: '30px',
                            }}
                          >
                            <ContractIcon
                              color="#5f8f31"
                              width={13}
                              height={13}
                            />
                          </span>
                        )}
                        {Icon && (
                          <Icon
                            width={40}
                            height={40}
                          />
                        )}
                        <Typography
                          fontSize={11}
                          marginTop={1}
                        >
                          {productCode}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                  background: isPackageSelected
                    ? 'linear-gradient(134deg, rgb(25 68 133) 0%, rgb(91 142 220) 40%, rgb(52 95 158) 60%, rgb(4 57 137) 100%)'
                    : 'linear-gradient(135deg, rgb(84 84 84 / 0%) 0%, rgb(204 202 202 / 61%) 40%, rgb(111 110 110 / 0%) 60%, rgb(169 167 167 / 50%) 100%)',
                  color: isPackageSelected ? '#fff' : '#000',
                }}
              >
                <Box
                  mt="auto"
                  px={1}
                  py={1.5}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  width="100%"
                >
                  <Box>
                    <Typography
                      fontSize={15}
                      fontWeight={600}
                      letterSpacing="0.05em"
                    >
                      FINANCE
                    </Typography>
                    <Typography
                      fontSize={22}
                      fontWeight={700}
                    >
                      ${financeSummary.finance.toFixed(0)}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography
                      fontSize={20}
                      fontWeight={700}
                      ml={2}
                    >
                      ${financeSummary.costPerMonth}/mo
                    </Typography>
                  </Box>
                </Box>

                <SelectedButton
                  disableElevation
                  variant="contained"
                  isContracted={isContracted}
                  disabled={isContracted || (selectedCard && selectedCard.isSelected)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectCard(card);
                  }}
                >
                  {renderSelectButtonContent(card.cardName)}
                </SelectedButton>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Drawer
        anchor="bottom"
        open={isSelectedEntryOpen}
        onClose={() => {
          setIsSelectedEntryOpen(false);
          setSelectedEntry(null);
        }}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            overflowY: 'auto',
            p: 2,
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={() => {
              setIsSelectedEntryOpen(false);
              setSelectedEntry(null);
            }}
            sx={{ position: 'absolute', top: '-10px', right: 0, zIndex: 1 }}
          >
            <CloseIcon fontSize={'large'} />
          </IconButton>
          <Box sx={{ width: 40, height: 'fit-content', mx: 'auto', mb: 3, mt: 1 }} />
          {selectedEntry && <SelectedEntryPanel selectedEntry={selectedEntry} />}
        </Box>
      </Drawer>
    </Box>
  );

  return isLoading ? (
    <Loader />
  ) : (
    <>
      {Array.isArray(customerCards) && customerCards.length > 0 ? (
        <>
          {isContracted && (
            <Box
              sx={{
                mb: 2,
                mt: 0.5,
                p: 1.5,
                borderRadius: 2,
                background: 'rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <LockIcon
                fontSize="small"
                sx={{ color: '#666' }}
              />
              <Typography
                fontSize={13}
                color="text.secondary"
              >
                This deal is contracted — selections are locked.
              </Typography>
            </Box>
          )}
          {isMobile ? mobileView() : desktopView()}

          {isMobile && (
            <Box
              sx={{
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                background: 'white',
                boxShadow: '0 -2px 12px rgba(0,0,0,0.10)',
                p: 1.5,
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => setModalType('decline')}
                disabled={isContracted || isDeclining}
                sx={{ borderRadius: 50, px: 2.5, py: 0.5, fontSize: '0.8rem' }}
              >
                {isDeclining ? (
                  <CircularProgress
                    size={20}
                    color="inherit"
                  />
                ) : (
                  'Decline All'
                )}
              </Button>

              <Button
                variant="contained"
                fullWidth
                disabled={!selectedPackage || isContracted || isSubmitting || (selectedCard && selectedCard.isSelected)}
                onClick={() => setModalType('submit')}
                sx={{ borderRadius: 50, px: 2.5, py: 0.5, fontSize: '0.8rem' }}
              >
                {isSubmitting ? (
                  <CircularProgress
                    size={20}
                    color="inherit"
                  />
                ) : (
                  'Submit'
                )}
              </Button>
            </Box>
          )}
          <Popover
            id="mouse-over-popover"
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              pointerEvents: isMobile ? 'auto' : 'none',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Typography sx={{ p: 1 }}>This Product is contracted</Typography>
          </Popover>
          {modalType && (
            <AlertModal
              open={!!modalType}
              title={modalType === 'submit' ? 'Confirm Selection' : 'Decline All Products'}
              subtitle={
                modalType === 'submit'
                  ? `Are you sure you want to select ${currentPackage?.cardName ?? ''} coverage?`
                  : 'Are you sure you want to decline all products?'
              }
              cancelButtonText="No"
              buttonText="Yes"
              isLoading={modalType === 'submit' ? isSubmitting : isDeclining}
              onClose={handleClose}
              onConfirm={modalType === 'submit' ? onConfirm : handleDeclineAll}
            />
          )}
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            textAlign: 'center',
          }}
        >
          <Typography fontSize={20}>No products available right now.</Typography>
        </Box>
      )}
    </>
  );
};

export default Products;
